/**
 * Shared status widgets used across define, render, and ref features.
 * - shieldGroupEvents: blocks mouse events from reaching CodeMirror
 * - createSyntaxBadge: factory for L0 letter-badge with optional sub/superscript
 * - StatusBadgeWidget: colored indicator with optional duration (L1/L2)
 * - StatusDotWidget: compact colored dot (L0)
 */

import { WidgetType } from "@codemirror/view";
import type { ExecutionStatus } from "../../../store/plugin-execution-store";

const statusDotWidgetCache = new Map<ExecutionStatus, StatusDotWidget>();
const statusBadgeWidgetCache = new Map<string, StatusBadgeWidget>();
const STATUS_BADGE_WIDGET_CACHE_LIMIT = 128;

export interface PrettySyntaxShellOptions {
    wrapperClass: string;
    badge: HTMLElement;
    primary: HTMLElement[];
    detail?: HTMLElement | null;
}

interface SyntaxEditInteractionOptions {
    keyboard?: boolean;
    position?: number;
}

function isSyntaxNavigationMouseEvent(event: MouseEvent): boolean {
    return event.button === 0 && (event.metaKey || event.ctrlKey);
}

/* ── shieldGroupEvents — prevent mouse events from reaching CodeMirror ── */
/**
 * Attaches stopPropagation listeners for common mouse events on a button group
 * container, preventing CodeMirror from interpreting clicks as cursor placements
 * or fold toggles.
 *
 * Used by all ButtonGroupWidget variants (define, for, content, import, use, meta).
 */
export function shieldGroupEvents(element: HTMLElement): void {
    for (const evtName of [
        "mousedown",
        "mouseup",
        "click",
        "dblclick",
        "pointerdown",
    ] as const) {
        element.addEventListener(evtName, (e) => {
            e.stopPropagation();
        });
    }
}

/* ── createSyntaxBadge — builds a letter badge DOM element ── */
/**
 * Creates a compact syntax-badge `<span>` with:
 * - main letter (centre)
 * - optional subscript (bottom-right, smaller)  → subtype qualifier
 * - optional superscript (top-right, smaller)   → external-import marker "M"
 * - optional template treatment                 → dashed blueprint badge
 *
 * Callers must still add a per-type CSS class for the background colour.
 */
export function createSyntaxBadge(
    main: string,
    opts?: { sub?: string; sup?: string; template?: boolean },
): HTMLElement {
    const el = document.createElement("span");
    el.className = "cm-magic-syntax-badge";
    if (opts?.template) {
        el.classList.add("cm-magic-syntax-badge-template");
    }

    const mainSpan = document.createElement("span");
    mainSpan.className = "cm-magic-syntax-badge-main";
    mainSpan.textContent = main;
    el.appendChild(mainSpan);

    // Right aside column: stacks sup (top) and sub (bottom) vertically
    const subText = opts?.sub;
    const supText = opts?.sup;
    const hasSub = !!subText;
    const hasSup = !!supText;
    if (hasSub || hasSup) {
        const aside = document.createElement("span");
        aside.className = "cm-magic-syntax-badge-aside";
        if (hasSup) {
            const sup = document.createElement("span");
            sup.className = "cm-magic-syntax-badge-sup";
            sup.textContent = supText ?? "";
            aside.appendChild(sup);
        }
        if (hasSub) {
            const sub = document.createElement("span");
            sub.className = "cm-magic-syntax-badge-sub";
            sub.textContent = subText ?? "";
            aside.appendChild(sub);
        }
        el.appendChild(aside);
    }
    return el;
}

export function createTextSpan(className: string, text: string): HTMLElement {
    const span = document.createElement("span");
    span.className = className;
    span.textContent = text;
    return span;
}

export function createHoverDetail(
    className: string,
    children: Array<HTMLElement | null | undefined>,
): HTMLElement | null {
    const items = children.filter((child): child is HTMLElement => !!child);
    if (items.length === 0) return null;

    const detail = document.createElement("span");
    detail.className = `cm-magic-hover-detail ${className}`;
    for (const child of items) {
        detail.appendChild(child);
    }
    return detail;
}

export function createWatchIndicator(unitId: string): HTMLElement {
    const indicator = document.createElement("span");
    indicator.className = "cm-magic-watch-indicator";
    indicator.dataset.magicWatchIndicator = unitId;
    indicator.title = `Watching ${unitId}`;
    indicator.setAttribute("aria-label", indicator.title);

    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");

    const eye = document.createElementNS(ns, "path");
    eye.setAttribute(
        "d",
        "M2.06 12.35a1 1 0 0 1 0-.7A11.75 11.75 0 0 1 12 4a11.75 11.75 0 0 1 9.94 7.65 1 1 0 0 1 0 .7A11.75 11.75 0 0 1 12 20a11.75 11.75 0 0 1-9.94-7.65Z",
    );
    svg.appendChild(eye);

    const pupil = document.createElementNS(ns, "circle");
    pupil.setAttribute("cx", "12");
    pupil.setAttribute("cy", "12");
    pupil.setAttribute("r", "3");
    svg.appendChild(pupil);

    indicator.appendChild(svg);
    return indicator;
}

export function createPrettySyntaxShell({
    wrapperClass,
    badge,
    primary,
    detail,
}: PrettySyntaxShellOptions): HTMLElement {
    const wrapper = document.createElement("span");
    wrapper.className = wrapperClass;

    const content = document.createElement("span");
    content.className = "cm-magic-pretty-content";

    const primaryGroup = document.createElement("span");
    primaryGroup.className = "cm-magic-pretty-primary";

    wrapper.appendChild(badge);
    for (const item of primary) {
        primaryGroup.appendChild(item);
    }
    content.appendChild(primaryGroup);
    if (detail) {
        content.appendChild(detail);
    }
    wrapper.appendChild(content);
    return wrapper;
}

export type PrettyEndKind = "define" | "meta" | "use" | "content" | "for" | "import";

const END_BADGE: Record<PrettyEndKind, string> = {
    define: "D",
    meta: "M",
    use: "U",
    content: "C",
    for: "F",
    import: "I",
};

export class PrettyEndWidget extends WidgetType {
    constructor(
        readonly kind: PrettyEndKind,
        readonly name?: string | null,
    ) {
        super();
    }

    eq(other: PrettyEndWidget): boolean {
        return other.kind === this.kind && other.name === this.name;
    }

    toDOM(): HTMLElement {
        const badge = createSyntaxBadge(END_BADGE[this.kind]);
        badge.classList.add(
            "cm-magic-end-pretty-icon",
            `cm-magic-end-pretty-icon-${this.kind}`,
        );

        const label =
            this.kind === "import"
                ? `end import${this.name ? ` ${this.name}` : ""}`
                : `end ${this.name || this.kind}`;

        const wrapper = createPrettySyntaxShell({
            wrapperClass: `cm-magic-end-pretty cm-magic-end-pretty-${this.kind}`,
            badge,
            primary: [
                createTextSpan(
                    "cm-magic-end-pretty-label",
                    label,
                ),
            ],
            detail: null,
        });
        return wrapper;
    }

    ignoreEvent(): boolean {
        return false;
    }
}

export function attachSyntaxEditInteractions(
    element: HTMLElement,
    options: SyntaxEditInteractionOptions = {},
): void {
    const keyboard = options.keyboard ?? true;

    if (keyboard) {
        element.tabIndex = 0;
        element.setAttribute("role", "button");
        element.setAttribute("aria-haspopup", "false");
    }

    const dispatchEnterEdit = () => {
        window.dispatchEvent(
            new CustomEvent("magic-md:enter-syntax-edit", {
                detail: { target: element, position: options.position },
            }),
        );
    };

    element.addEventListener("mousedown", (event) => {
        if (isSyntaxNavigationMouseEvent(event)) return;
        event.preventDefault();
        event.stopPropagation();
        dispatchEnterEdit();
    });

    if (keyboard) {
        element.addEventListener("keydown", (event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            event.stopPropagation();
            dispatchEnterEdit();
        });
    }
}

export function createInlineRenderToggleButton(
    unitId: string,
    inlineRendered: boolean,
    title: string,
): HTMLElement {
    const btn = document.createElement("span");
    btn.className = inlineRendered
        ? "cm-magic-btn cm-magic-btn-inline-render cm-magic-btn-active"
        : "cm-magic-btn cm-magic-btn-inline-render";
    btn.title = title;
    btn.setAttribute(
        "aria-label",
        inlineRendered
            ? `Hide inline render ${unitId}`
            : `Show inline render ${unitId}`,
    );

    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", "11");
    svg.setAttribute("height", "11");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");

    const eye = document.createElementNS(ns, "path");
    eye.setAttribute(
        "d",
        "M2.06 12.35a1 1 0 0 1 0-.7A11.75 11.75 0 0 1 12 4a11.75 11.75 0 0 1 9.94 7.65 1 1 0 0 1 0 .7A11.75 11.75 0 0 1 12 20a11.75 11.75 0 0 1-9.94-7.65Z",
    );
    svg.appendChild(eye);

    const pupil = document.createElementNS(ns, "circle");
    pupil.setAttribute("cx", "12");
    pupil.setAttribute("cy", "12");
    pupil.setAttribute("r", "3");
    svg.appendChild(pupil);
    btn.appendChild(svg);

    btn.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const detail: { definitionId: string; sourceElement?: HTMLElement } = {
            definitionId: unitId,
        };
        Object.defineProperty(detail, "sourceElement", {
            value: btn,
            enumerable: false,
        });
        window.dispatchEvent(
            new CustomEvent("magic-md:toggle-inline-render", {
                detail,
            }),
        );
    });

    return btn;
}

export function createTemplateInputsButton(
    unitKind: "define" | "content" | "for" | "use",
    unitId: string,
    title: string,
): HTMLElement {
    const btn = document.createElement("span");
    btn.className = "cm-magic-btn cm-magic-btn-inputs";
    btn.style.position = "relative";
    btn.style.zIndex = "4";
    btn.title = title;
    btn.setAttribute("aria-label", `Edit inputs ${unitId}`);

    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", "11");
    svg.setAttribute("height", "11");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");

    for (const d of ["M4 7h16", "M4 12h10", "M4 17h7"]) {
        const path = document.createElementNS(ns, "path");
        path.setAttribute("d", d);
        svg.appendChild(path);
    }
    btn.appendChild(svg);

    btn.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = btn.getBoundingClientRect();
        const detail: {
            unitKind: "define" | "content" | "for" | "use";
            unitId: string;
            x: number;
            y: number;
            sourceElement?: HTMLElement;
        } = {
            unitKind,
            unitId,
            x: rect.left,
            y: rect.bottom,
        };
        Object.defineProperty(detail, "sourceElement", {
            value: btn,
            enumerable: false,
        });
        window.dispatchEvent(
            new CustomEvent("magic-md:edit-template-inputs", { detail }),
        );
    });

    return btn;
}

export function createClearExecutionButton(
    unitId: string,
    title: string,
    ariaLabel: string,
): HTMLElement {
    const btn = document.createElement("span");
    btn.className = "cm-magic-btn cm-magic-btn-clear";
    btn.textContent = "×";
    btn.title = title;
    btn.setAttribute("aria-label", ariaLabel);
    btn.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.dispatchEvent(
            new CustomEvent("magic-md:clear-execution", {
                detail: { unitId },
            }),
        );
    });
    return btn;
}

export class StatusBadgeWidget extends WidgetType {
    constructor(
        readonly status: ExecutionStatus,
        readonly duration: number | null,
    ) {
        super();
    }
    eq(other: StatusBadgeWidget) {
        return other.status === this.status && other.duration === this.duration;
    }
    toDOM(): HTMLElement {
        return createStatusBadgeElement(this.status, this.duration);
    }
    ignoreEvent(): boolean {
        return true;
    }
}

export function createStatusBadgeElement(
    status: ExecutionStatus,
    duration: number | null,
): HTMLElement {
    const span = document.createElement("span");
    span.className = `cm-magic-badge cm-magic-badge-${status}`;
    switch (status) {
        case "running": {
            const dot = document.createElement("span");
            dot.className = "cm-magic-badge-dot";
            span.appendChild(dot);
            span.appendChild(document.createTextNode(" Running"));
            span.title = "Running…";
            break;
        }
        case "success":
            span.textContent =
                duration != null ? `✓ ${duration}ms` : "✓ Done";
            span.title =
                duration != null ? `Completed in ${duration}ms` : "Success";
            break;
        case "error":
            span.textContent = "✗ Error";
            span.title = "Error";
            break;
        case "cached":
            return span; // cached from parsed output — no badge
        default:
            return span; // idle — render empty
    }
    return span;
}

export class StatusDotWidget extends WidgetType {
    constructor(readonly status: ExecutionStatus) {
        super();
    }
    eq(other: StatusDotWidget) {
        return other.status === this.status;
    }
    toDOM(): HTMLElement {
        return createStatusDotElement(this.status);
    }
    ignoreEvent(): boolean {
        return true;
    }
}

export function createStatusDotElement(status: ExecutionStatus): HTMLElement {
    const span = document.createElement("span");
    span.className = `cm-magic-status-dot cm-magic-status-dot-${status}`;
    if (status === "idle" || status === "cached" || status === "success") {
        span.style.display = "none";
    }
    return span;
}

export function appendInlineStatus(
    wrapper: HTMLElement,
    status: ExecutionStatus,
    duration: number | null,
): void {
    if (status === "idle") return;

    wrapper.classList.add("cm-magic-inline-status-host");
    if (status !== "cached") {
        wrapper.appendChild(createStatusDotElement(status));
    }
    wrapper.appendChild(createStatusBadgeElement(status, duration));
}

export function getCachedStatusDotWidget(status: ExecutionStatus): StatusDotWidget {
    const cached = statusDotWidgetCache.get(status);
    if (cached) return cached;

    const widget = new StatusDotWidget(status);
    statusDotWidgetCache.set(status, widget);
    return widget;
}

export function getCachedStatusBadgeWidget(
    status: ExecutionStatus,
    duration: number | null,
): StatusBadgeWidget {
    const key = `${status}:${duration ?? "null"}`;
    const cached = statusBadgeWidgetCache.get(key);
    if (cached) {
        statusBadgeWidgetCache.delete(key);
        statusBadgeWidgetCache.set(key, cached);
        return cached;
    }

    const widget = new StatusBadgeWidget(status, duration);
    statusBadgeWidgetCache.set(key, widget);
    if (statusBadgeWidgetCache.size > STATUS_BADGE_WIDGET_CACHE_LIMIT) {
        const oldestKey = statusBadgeWidgetCache.keys().next().value;
        if (oldestKey !== undefined) statusBadgeWidgetCache.delete(oldestKey);
    }
    return widget;
}
